import { createContext, Dispatch, FC, PropsWithChildren, SetStateAction, useContext, useEffect, useEffectEvent, useMemo, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { useParams } from 'react-router'
import { useSnackbar } from 'notistack'

import Api from '../utils/api'
import { GLOBAL_API } from '../utils/constants'

import { useAuthentication } from './auth-provider'

const defaultAppContext = {
  pageRefreshCounter: 0,
  refreshPage: () => null,
  tree: undefined,
  treeRoots: [],
  roles: [],
  setTreeRefreshCounter: (() => {}) as Dispatch<SetStateAction<number>>,
  tenantDomainMap: new Map<number, string>(),
  host: '',
  tenantId: '',
  allTenants: [],
}

export const AppContext = createContext<{
  pageRefreshCounter: number
  refreshPage: () => void
  tree: IFolderTree | undefined
  treeRoots: number[]
  roles: IRoleType[]
  setTreeRefreshCounter: Dispatch<SetStateAction<number>>
  tenantDomainMap: Map<number, string>
  allTenants: ITenant[]
  host: string
  tenantId: string
}>(defaultAppContext)

const AppProvider: FC<PropsWithChildren> = ({ children }) => {
  const { isAuthenticated } = useAuthentication()
  const auth = useAuth()
  const params = useParams()

  const [pageRefreshCounter, setPageRefreshCounter] = useState<number>(0)

  const [tree, setTree] = useState<IFolderTree>()
  const [treeRoots, setTreeRoots] = useState<number[]>([])
  const [treeRefreshCounter, setTreeRefreshCounter] = useState<number>(0)

  const [defaultTenantId, setDefaultTenantId] = useState<number | null>(null)
  const [allTenants, setAllTenants] = useState<ITenant[]>([])

  const tenantDomainMap = useMemo(() => new Map<number, string>(), [])
  const tenantId = params.tenantId || String(defaultTenantId ?? '')
  const host = tenantDomainMap.has(Number(tenantId)) ? `https://${tenantDomainMap.get(Number(tenantId))}` : ''

  const [roles, setRoles] = useState<IRoleType[]>([])

  const { enqueueSnackbar } = useSnackbar()

  const refreshPage = () => setPageRefreshCounter((prev) => prev + 1)

  const fetchTenantData = useEffectEvent(async () => {
    if (!auth.user?.access_token) return

    const allFetchedTenants: ITenant[] = []

    const getTenants = async () => {
      const getTenantsApi = async (offset = 0) => {
        const response = await Api.get(`${GLOBAL_API}/api/v1/tenants?offset=${offset}&limit=100`)
        if (!response.ok) {
          if (response.status !== 401) enqueueSnackbar('Something went wrong. Please try again later')
          return response
        }
        response?.body?.rows?.forEach((tenant: { id: number; fqdns: { api: string; central: string } }) => {
          if (tenant.fqdns?.api) {
            tenantDomainMap.set(tenant.id, tenant.fqdns.api)
          }
        })

        allFetchedTenants.push(...(response?.body?.rows ?? []))

        if (response.body.hasMore) {
          // Call the function again (recursively continue to fetch tenants until hasMore is false)
          return getTenantsApi(offset + 100)
        }
        return response
      }

      const result = await getTenantsApi()
      setAllTenants(allFetchedTenants)
      return result
    }

    const currentTenantRes = await Api.get('/api/tenant')
    if (!currentTenantRes.ok) {
      enqueueSnackbar('Something went wrong. Please try again later')
      return
    }
    await getTenants()
    setDefaultTenantId(currentTenantRes.body.id)
  })

  // Fetch tenant domain mapping
  useEffect(() => {
    if (isAuthenticated && tenantDomainMap.size === 0) {
      fetchTenantData()
    }
  }, [isAuthenticated, tenantDomainMap, pageRefreshCounter])

  // Fetch folder tree
  const fetchTreeData = useEffectEvent(async () => {
    if (!host || !auth.user?.access_token) return
    const resTree = await Api.get(`${host}/api/folders/tree`)
    if (resTree.ok) {
      setTree(resTree.body.tree)
      setTreeRoots(resTree.body.roots)
    } else if (resTree.status !== 401) {
      enqueueSnackbar('Something went wrong and we could not load the folder tree at this time. Please try again later')
    }
  })
  useEffect(() => {
    if (isAuthenticated) fetchTreeData()
  }, [isAuthenticated, host, treeRefreshCounter, pageRefreshCounter])

  // Fetch roles
  useEffect(() => {
    const fetchData = async () => {
      if (!auth.user?.access_token) return
      const resRoles = await Api.get(`${host}/api/roles`)
      if (resRoles.ok) {
        setRoles(resRoles.body)
      } else if (resRoles.status !== 401) {
        enqueueSnackbar('Something went wrong and we could not load roles at this time. Please try again later', { preventDuplicate: true })
      }
    }

    if (isAuthenticated && roles.length === 0 && host) {
      fetchData()
    }
  }, [isAuthenticated, enqueueSnackbar, auth.user?.access_token, roles.length, host, pageRefreshCounter])

  return (
    <AppContext.Provider
      value={{ pageRefreshCounter, refreshPage, tree, treeRoots, roles, setTreeRefreshCounter, tenantDomainMap, host, tenantId, allTenants }}
    >
      {children}
    </AppContext.Provider>
  )
}

export default AppProvider

export const useHost = () => useContext(AppContext).host
